import axios from 'axios';

// Define types for authentication responses
interface LoginResponse {
  success: boolean;
  token: string;
  data: {
    _id: string;
    name: string;
    email: string;
    role: 'user' | 'admin';
  };
}

interface AuthResponse {
  success: boolean;
  data: any;
  error?: string;
}

// Base URL for API requests
const API_BASE_URL = '';

/**
 * Authenticate a user with email and password
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
      email,
      password
    });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, error: 'Authentication failed' };
  }
};

/**
 * Log out the current user
 */
export const logout = async (): Promise<AuthResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/auth/logout`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, error: 'Logout failed' };
  }
};

/**
 * Get the current user's profile
 */
export const getCurrentUser = async (): Promise<AuthResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, error: 'Failed to get user profile' };
  }
};

/**
 * Set auth token for future API requests
 */
export const setAuthToken = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

/**
 * Check if a user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('authToken');
};

/**
 * Forgot password - request a password reset
 */
export const forgotPassword = async (email: string): Promise<AuthResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/auth/forgotpassword`, { email });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, error: 'Password reset request failed' };
  }
};

/**
 * Reset password with token
 */
export const resetPassword = async (resetToken: string, password: string): Promise<AuthResponse> => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/auth/resetpassword/${resetToken}`,
      { password }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, error: 'Password reset failed' };
  }
};

export default {
  login,
  logout,
  getCurrentUser,
  setAuthToken,
  isAuthenticated,
  forgotPassword,
  resetPassword
}; 